import { ApiError } from '@agentic-os/contracts';

/**
 * Same-origin API client (BFF, ADR-005/006/024). The browser holds only the httpOnly session
 * cookie; no tokens are ever stored in JS-accessible storage. State-changing requests carry the
 * CSRF token obtained from /api/v1/session (kept in memory only).
 */
export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly correlationId: string | undefined,
    readonly safeMessage: string,
  ) {
    super(`${code} (HTTP ${status})`);
  }
}

let csrfToken: string | null = null;
export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export async function api<T>(
  path: string,
  init: { method?: 'GET' | 'POST' | 'PUT'; body?: unknown; acceptStatuses?: number[] } = {},
): Promise<T> {
  const method = init.method ?? 'GET';
  const headers: Record<string, string> = { accept: 'application/json' };
  if (init.body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET' && csrfToken) headers['x-csrf-token'] = csrfToken;
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers,
    ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  });
  const body: unknown = await res.json().catch(() => undefined);
  if (!res.ok && !(init.acceptStatuses ?? []).includes(res.status)) {
    const parsed = ApiError.safeParse(body);
    throw new ApiRequestError(
      res.status,
      parsed.success ? parsed.data.error.code : 'UNKNOWN',
      parsed.success ? parsed.data.error.correlationId : undefined,
      parsed.success ? parsed.data.error.message : 'The request failed',
    );
  }
  return body as T;
}

export function loginUrl(returnTo: string = window.location.pathname): string {
  return `/api/v1/auth/login?returnTo=${encodeURIComponent(returnTo)}`;
}

export function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function durationMs(start: string | null, end: string | null): string {
  if (!start || !end) return '—';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}
